import { createContext, useContext, type ReactNode } from 'react';

import { type CallSession, useCallSessionInstance } from './session';

const noop = () => undefined;

const CallContext = createContext<CallSession>({
  start: async () => undefined,
  accept: noop,
  reject: noop,
  hangUp: noop,
  toggleMicrophone: noop,
  toggleCamera: noop,
});

/**
 * Holds the single call session for the whole application.
 *
 * A call owns one `RTCPeerConnection`, one microphone grant and one set of
 * signalling subscriptions. Instantiating the session in more than one place
 * would give the chat header a peer connection that the call window knows
 * nothing about, so hanging up would leave the media running. One instance
 * lives here and everything else reads it through context.
 */
export function CallProvider({ children }: { children: ReactNode }) {
  const session = useCallSessionInstance();
  return <CallContext.Provider value={session}>{children}</CallContext.Provider>;
}

export function useCall(): CallSession {
  return useContext(CallContext);
}
